/**
 * Gluon API client for mobile promo pages (`numeo-spot://promo/<slug>`), `/v1/gluon/mobile-promo-pages`.
 * Edits go to a draft; the app is only served the published page.
 */

import axios from 'axios'
import type { PromoPageContent, PromoPageEditorView, PromoPageSummary } from '@numeo/types-main-mobile'

interface Envelope<T> {
    success: boolean
    data: T
}

const BASE = '/gluon/mobile-promo-pages'
const pagePath = (slug: string) => `${BASE}/${encodeURIComponent(slug)}`

export const mobilePromoPagesApi = {
    list: async (): Promise<PromoPageSummary[]> => (await axios.get<Envelope<PromoPageSummary[]>>(`${BASE}/v1`)).data.data,

    /** Null when the slug has never been saved. */
    get: async (slug: string): Promise<PromoPageEditorView | null> => {
        try {
            return (await axios.get<Envelope<PromoPageEditorView>>(`${pagePath(slug)}/v1`)).data.data
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) return null
            throw err
        }
    },

    saveDraft: async (slug: string, page: PromoPageContent): Promise<PromoPageEditorView> =>
        (await axios.put<Envelope<PromoPageEditorView>>(`${pagePath(slug)}/v1`, { page })).data.data,

    publish: async (slug: string): Promise<PromoPageEditorView> => (await axios.post<Envelope<PromoPageEditorView>>(`${pagePath(slug)}/publish/v1`)).data.data,

    unpublish: async (slug: string): Promise<PromoPageEditorView> =>
        (await axios.post<Envelope<PromoPageEditorView>>(`${pagePath(slug)}/unpublish/v1`)).data.data,

    rollback: async (slug: string, versionIndex: number): Promise<PromoPageEditorView> =>
        (await axios.post<Envelope<PromoPageEditorView>>(`${pagePath(slug)}/rollback/v1`, { versionIndex })).data.data,
}
